import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FileTextIcon, SearchIcon, BookIcon, HelpCircleIcon, MessageSquareIcon, InfoIcon, BookOpenIcon, FileIcon, FolderIcon, TagIcon, ChevronRightIcon, ChevronDownIcon, ExternalLinkIcon, DownloadIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, ArrowRightIcon, ThumbsUpIcon, ThumbsDownIcon, PlusIcon, BookmarkIcon, StarIcon, ClipboardIcon } from 'lucide-react';
import { mockPolicyDocuments, mockChatbotResponses } from '../utils/mockData';
export function KnowledgeBase() {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaqs, setExpandedFaqs] = useState([]);
  const [activeTab, setActiveTab] = useState('documents');
  const [chatMessages, setChatMessages] = useState([{
    id: 1,
    type: 'system',
    content: 'Welcome to the Expense Policy Assistant! How can I help you today?'
  }]);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [recentSearches, setRecentSearches] = useState(['per diem rates', 'receipt requirements', 'approval workflow']);
  const [favoriteDocuments, setFavoriteDocuments] = useState([1, 3]); // IDs of favorite documents
  const chatEndRef = useRef(null);
  const searchInputRef = useRef(null);
  // Filter documents based on search and category
  const filteredDocuments = mockPolicyDocuments.filter(doc => {
    const matchesSearch = searchTerm === '' || doc.name.toLowerCase().includes(searchTerm.toLowerCase()) || doc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || doc.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  // Get unique categories from documents
  const categories = ['all', ...new Set(mockPolicyDocuments.map(doc => doc.category))];
  // Format file size
  const formatFileSize = bytes => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  // Format date
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Toggle FAQ expansion
  const toggleFaqExpansion = index => {
    if (expandedFaqs.includes(index)) {
      setExpandedFaqs(expandedFaqs.filter(i => i !== index));
    } else {
      setExpandedFaqs([...expandedFaqs, index]);
    }
  };
  // Handle chat message submission
  const handleChatSubmit = e => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    // Add user message
    const userMessage = {
      id: chatMessages.length + 1,
      type: 'user',
      content: messageInput
    };
    setChatMessages([...chatMessages, userMessage]);
    setMessageInput('');
    setIsTyping(true);
    // Simulate response
    setTimeout(() => {
      setIsTyping(false);
      const botResponse = {
        id: chatMessages.length + 2,
        type: 'bot',
        content: mockChatbotResponses.getResponse(messageInput)
      };
      setChatMessages(prev => [...prev, botResponse]);
      // Scroll to bottom
      chatEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 1500);
  };
  // Handle search submission
  const handleSearchSubmit = e => {
    e.preventDefault();
    if (searchTerm.trim() && !recentSearches.includes(searchTerm.trim())) {
      setRecentSearches(prev => [searchTerm.trim(), ...prev.slice(0, 4)]);
    }
  };
  // Toggle document favorite status
  const toggleFavorite = id => {
    if (favoriteDocuments.includes(id)) {
      setFavoriteDocuments(favoriteDocuments.filter(docId => docId !== id));
    } else {
      setFavoriteDocuments([...favoriteDocuments, id]);
    }
  };
  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chatMessages]);
  // Focus search input when tab changes to documents or faqs
  useEffect(() => {
    if ((activeTab === 'documents' || activeTab === 'faqs') && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [activeTab]);
  // FAQs data
  const faqs = [{
    question: 'What are the per diem rates for meals and lodging?',
    answer: 'Per diem rates are based on GSA standards and vary by location. For most major cities, the meal breakdown is approximately: Breakfast $17, Lunch $18, Dinner $34, and Incidentals $10 per day. Lodging rates range from $96 to $304 depending on the city and season. You can view the specific rates for your destination in the expense submission form.'
  }, {
    question: 'When do I need to submit receipts with my expenses?',
    answer: 'Receipts are required for all expenses over $25. For meals, an itemized receipt showing all items purchased is required, not just the credit card slip. For airfare, you need both the receipt and boarding pass. Digital copies (clear photos or scans) are acceptable.'
  }, {
    question: 'What is the deadline for submitting expenses?',
    answer: 'Expense reports should be submitted within 30 days of incurring the expense. Expenses submitted after 60 days may not be reimbursed without VP approval. The finance team processes approved expenses every Friday, with reimbursements typically appearing in your bank account by the following Wednesday.'
  }, {
    question: 'How does the expense approval process work?',
    answer: "After submission, your expense report will be reviewed by your direct manager. Expenses over $500 require additional approval from the department head. The finance team performs a final compliance check before processing payment. You'll receive notifications at each approval stage via email and in the app."
  }, {
    question: 'Are alcoholic beverages reimbursable?',
    answer: 'Alcoholic beverages are not reimbursable as standalone expenses. If included with a business meal, alcohol should be reasonable and not exceed 20% of the total meal cost. Itemized receipts are required to distinguish between food and alcohol charges.'
  }, {
    question: 'What information do I need to provide for business meals with attendees?',
    answer: 'For business meals with external parties, you must list all attendees, their organizations, and the business purpose. For internal team meals, list all employee names and the business purpose. Team meals without a specific business purpose are not reimbursable.'
  }, {
    question: 'How are international expenses handled?',
    answer: "International travel requires pre-approval through the Travel Authorization form. Exchange rates are calculated based on the date of the expense using the company's financial system. Submit receipts in the original currency along with the credit card statement showing the USD amount charged."
  }, {
    question: 'What transportation expenses are covered?',
    answer: 'For air travel, book economy class at least 14 days in advance when possible. For rental cars, choose economy or compact vehicles. Rideshare services are preferred over taxis when available. Personal vehicle mileage is reimbursed at the current IRS rate of $0.655 per mile.'
  }];
  // Quick links data
  const quickLinks = [{
    title: 'Submit a New Expense',
    icon: <FileTextIcon size={18} data-id="element-1866" />,
    path: '/expense/new',
    description: 'Create and submit a new expense report'
  }, {
    title: 'View Your Expenses',
    icon: <FolderIcon size={18} data-id="element-1867" />,
    path: '/dashboard',
    description: 'See all your submitted and pending expenses'
  }, {
    title: 'Per Diem Calculator',
    icon: <InfoIcon size={18} data-id="element-1868" />,
    path: '#',
    description: 'Calculate allowable amounts for your destination'
  }, {
    title: 'Contact Finance Team',
    icon: <MessageSquareIcon size={18} data-id="element-1869" />,
    path: '#',
    description: 'Get help with expense-related questions'
  }];
  // Render documents tab
  const renderDocumentsTab = () => {
    return <div className="space-y-6" data-id="element-1870">
        <div className="flex flex-col md:flex-row gap-4 items-start" data-id="element-1871">
          <div className="w-full md:w-64 flex-shrink-0" data-id="element-1872">
            <Card className="p-4" data-id="element-1873">
              <h3 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1874">
                Categories
              </h3>
              <ul className="space-y-1" data-id="element-1875">
                {categories.map(category => <li key={category} data-id="element-1876">
                    <button onClick={() => setActiveCategory(category)} className={`flex items-center w-full px-3 py-2 text-sm rounded-md ${activeCategory === category ? 'bg-blue-900/30 text-blue-300' : 'text-gray-300 hover:bg-gray-700'}`} data-id="element-1877">
                      {category === 'all' ? 'All Documents' : category}
                    </button>
                  </li>)}
              </ul>
              {favoriteDocuments.length > 0 && <div className="mt-6" data-id="element-1878">
                  <h3 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1879">
                    Favorites
                  </h3>
                  <ul className="space-y-1" data-id="element-1880">
                    {mockPolicyDocuments.filter(doc => favoriteDocuments.includes(doc.id)).map(doc => <li key={`fav-${doc.id}`} data-id="element-1881">
                          <a href="#" className="flex items-center px-3 py-2 text-sm rounded-md text-gray-300 hover:bg-gray-700" data-id="element-1882">
                            <StarIcon size={16} className="mr-2 text-yellow-500" data-id="element-1883" />
                            <span className="truncate" data-id="element-1884">{doc.name}</span>
                          </a>
                        </li>)}
                  </ul>
                </div>}
              <div className="mt-6" data-id="element-1885">
                <h3 className="text-sm font-medium text-gray-300 mb-3" data-id="element-1886">
                  Quick Links
                </h3>
                <ul className="space-y-2" data-id="element-1887">
                  {quickLinks.map((link, index) => <li key={index} data-id="element-1888">
                      <a href={link.path} className="flex items-center text-sm text-gray-300 hover:text-blue-300 group" data-id="element-1889">
                        <span className="mr-2 text-gray-400 group-hover:text-blue-400" data-id="element-1890">
                          {link.icon}
                        </span>
                        {link.title}
                      </a>
                    </li>)}
                </ul>
              </div>
            </Card>
          </div>
          <div className="flex-1" data-id="element-1891">
            <Card className="p-6" data-id="element-1892">
              <form onSubmit={handleSearchSubmit} className="mb-6" data-id="element-1893">
                <div className="relative" data-id="element-1894">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1895">
                    <SearchIcon size={18} className="text-gray-400" data-id="element-1896" />
                  </div>
                  <input ref={searchInputRef} type="text" placeholder="Search documents..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1897" />
                </div>
                {recentSearches.length > 0 && searchTerm === '' && <div className="mt-2" data-id="element-1898">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-1899">
                      Recent searches:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-1900">
                      {recentSearches.map((term, index) => <button key={index} type="button" onClick={() => setSearchTerm(term)} className="text-xs bg-gray-700 border border-gray-600 rounded-full px-3 py-1 text-gray-300 hover:bg-gray-600 transition-colors" data-id="element-1901">
                          {term}
                        </button>)}
                    </div>
                  </div>}
              </form>
              {filteredDocuments.length > 0 ? <div className="space-y-4" data-id="element-1902">
                  {filteredDocuments.map(document => <div key={document.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-750 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors" data-id="element-1903">
                      <div className="flex items-start" data-id="element-1904">
                        <div className="flex-shrink-0 h-10 w-10 rounded-md bg-gray-700 border border-gray-600 flex items-center justify-center" data-id="element-1905">
                          {document.category === 'policy' ? <FileTextIcon size={20} className="text-blue-400" data-id="element-1906" /> : document.category === 'guide' ? <BookOpenIcon size={20} className="text-green-400" data-id="element-1907" /> : document.category === 'form' ? <ClipboardIcon size={20} className="text-yellow-400" data-id="element-1908" /> : <FileIcon size={20} className="text-gray-400" data-id="element-1909" />}
                        </div>
                        <div className="ml-4" data-id="element-1910">
                          <div className="flex items-center" data-id="element-1911">
                            <h4 className="text-sm font-medium text-gray-200" data-id="element-1912">
                              {document.name}
                            </h4>
                            <button onClick={() => toggleFavorite(document.id)} className="ml-2 text-gray-400 hover:text-yellow-500 focus:outline-none" aria-label={favoriteDocuments.includes(document.id) ? 'Remove from favorites' : 'Add to favorites'} data-id="element-1913">
                              <StarIcon size={16} className={favoriteDocuments.includes(document.id) ? 'text-yellow-500 fill-yellow-500' : ''} data-id="element-1914" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-1915">
                            {document.description}
                          </p>
                          <div className="flex items-center mt-2" data-id="element-1916">
                            <span className="text-xs text-gray-500" data-id="element-1917">
                              <TagIcon size={12} className="inline mr-1" data-id="element-1918" />
                              {document.category.charAt(0).toUpperCase() + document.category.slice(1)}
                            </span>
                            <span className="mx-2 text-gray-600" data-id="element-1919">•</span>
                            <span className="text-xs text-gray-500" data-id="element-1920">
                              {formatFileSize(document.size)}
                            </span>
                            <span className="mx-2 text-gray-600" data-id="element-1921">•</span>
                            <span className="text-xs text-gray-500" data-id="element-1922">
                              <ClockIcon size={12} className="inline mr-1" data-id="element-1923" />
                              {formatDate(document.uploadDate)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex mt-4 md:mt-0 ml-14 md:ml-0" data-id="element-1924">
                        <Button variant="outline" size="sm" data-id="element-1925">
                          <DownloadIcon size={14} className="mr-1.5" data-id="element-1926" />
                          Download
                        </Button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12" data-id="element-1927">
                  <FileIcon size={48} className="mx-auto text-gray-500 mb-4" data-id="element-1928" />
                  <h3 className="text-lg font-medium text-gray-300" data-id="element-1929">
                    No documents found
                  </h3>
                  <p className="text-gray-400 mt-2" data-id="element-1930">
                    Try adjusting your search or filter to find what you're
                    looking for
                  </p>
                </div>}
            </Card>
          </div>
        </div>
      </div>;
  };
  // Render FAQs tab
  const renderFaqsTab = () => {
    return <div className="space-y-6" data-id="element-1931">
        <Card className="p-6" data-id="element-1932">
          <div className="mb-6" data-id="element-1933">
            <div className="relative" data-id="element-1934">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1935">
                <SearchIcon size={18} className="text-gray-400" data-id="element-1936" />
              </div>
              <input ref={searchInputRef} type="text" placeholder="Search frequently asked questions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1937" />
            </div>
            {recentSearches.length > 0 && searchTerm === '' && <div className="mt-2" data-id="element-1938">
                <p className="text-xs text-gray-400 mb-1" data-id="element-1939">Recent searches:</p>
                <div className="flex flex-wrap gap-2" data-id="element-1940">
                  {recentSearches.map((term, index) => <button key={index} type="button" onClick={() => setSearchTerm(term)} className="text-xs bg-gray-700 border border-gray-600 rounded-full px-3 py-1 text-gray-300 hover:bg-gray-600 transition-colors" data-id="element-1941">
                      {term}
                    </button>)}
                </div>
              </div>}
          </div>
          <div className="space-y-4" data-id="element-1942">
            {faqs.filter(faq => searchTerm === '' || faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase())).map((faq, index) => <div key={index} className="border border-gray-700 rounded-lg overflow-hidden" data-id="element-1943">
                  <button onClick={() => toggleFaqExpansion(index)} className="flex items-center justify-between w-full px-6 py-4 text-left bg-gray-750 hover:bg-gray-700 transition-colors" aria-expanded={expandedFaqs.includes(index)} aria-controls={`faq-answer-${index}`} data-id="element-1944">
                    <div className="flex items-center" data-id="element-1945">
                      <HelpCircleIcon size={18} className="text-blue-400 mr-3 flex-shrink-0" data-id="element-1946" />
                      <span className="text-sm font-medium text-gray-200" data-id="element-1947">
                        {faq.question}
                      </span>
                    </div>
                    <span className="flex-shrink-0 ml-2" data-id="element-1948">
                      {expandedFaqs.includes(index) ? <ChevronDownIcon size={18} className="text-gray-400" data-id="element-1949" /> : <ChevronRightIcon size={18} className="text-gray-400" data-id="element-1950" />}
                    </span>
                  </button>
                  {expandedFaqs.includes(index) && <div id={`faq-answer-${index}`} className="px-6 py-4 bg-gray-800 border-t border-gray-700" data-id="element-1951">
                      <p className="text-sm text-gray-300 pl-8" data-id="element-1952">{faq.answer}</p>
                      <div className="mt-3 flex items-center justify-end text-xs text-gray-400" data-id="element-1953">
                        <span data-id="element-1954">Was this helpful?</span>
                        <button className="ml-3 p-1 hover:bg-gray-700 rounded" aria-label="Thumbs up" data-id="element-1955">
                          <ThumbsUpIcon size={14} data-id="element-1956" />
                        </button>
                        <button className="ml-1 p-1 hover:bg-gray-700 rounded" aria-label="Thumbs down" data-id="element-1957">
                          <ThumbsDownIcon size={14} data-id="element-1958" />
                        </button>
                      </div>
                    </div>}
                </div>)}
            {faqs.filter(faq => searchTerm === '' || faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && <div className="text-center py-12" data-id="element-1959">
                <HelpCircleIcon size={48} className="mx-auto text-gray-500 mb-4" data-id="element-1960" />
                <h3 className="text-lg font-medium text-gray-300" data-id="element-1961">
                  No FAQs found
                </h3>
                <p className="text-gray-400 mt-2" data-id="element-1962">
                  Try adjusting your search or ask a question in the chat
                  assistant
                </p>
                <Button onClick={() => setActiveTab('chat')} variant="outline" className="mt-4" data-id="element-1963">
                  <MessageSquareIcon size={16} className="mr-2" data-id="element-1964" />
                  Go to Chat Assistant
                </Button>
              </div>}
          </div>
          <div className="mt-8 bg-blue-900/30 border border-blue-800 rounded-md p-4 flex items-start" data-id="element-1965">
            <InfoIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-1966" />
            <div data-id="element-1967">
              <h3 className="text-sm font-medium text-blue-300" data-id="element-1968">
                Can't find what you're looking for?
              </h3>
              <p className="text-sm text-blue-200 mt-1" data-id="element-1969">
                Try using the chat assistant to get answers to your specific
                questions or contact the finance team directly.
              </p>
              <button onClick={() => setActiveTab('chat')} className="mt-2 inline-flex items-center text-sm text-blue-300 hover:text-blue-200" data-id="element-1970">
                Go to Chat Assistant
                <ArrowRightIcon size={14} className="ml-1.5" data-id="element-1971" />
              </button>
            </div>
          </div>
        </Card>
      </div>;
  };
  // Render chat assistant tab
  const renderChatTab = () => {
    return <div className="space-y-6" data-id="element-1972">
        <Card className="p-0 overflow-hidden flex flex-col h-[600px]" data-id="element-1973">
          <div className="bg-gray-750 border-b border-gray-700 p-4" data-id="element-1974">
            <div className="flex items-center" data-id="element-1975">
              <MessageSquareIcon size={20} className="text-blue-400 mr-2" data-id="element-1976" />
              <h3 className="text-md font-medium text-gray-200" data-id="element-1977">
                Expense Policy Assistant
              </h3>
            </div>
            <p className="text-xs text-gray-400 mt-1" data-id="element-1978">
              Ask questions about expense policies, per diem rates, and
              reimbursement procedures
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-1979">
            {chatMessages.map(message => <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1980">
                <div className={`max-w-[80%] rounded-lg px-4 py-2 ${message.type === 'user' ? 'bg-blue-600 text-white' : message.type === 'system' ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-750 text-gray-200 border border-gray-700'}`} data-id="element-1981">
                  {message.content}
                </div>
              </div>)}
            {isTyping && <div className="flex justify-start" data-id="element-1982">
                <div className="max-w-[80%] rounded-lg px-4 py-2 bg-gray-750 text-gray-200 border border-gray-700" data-id="element-1983">
                  <div className="flex space-x-1 items-center h-5" data-id="element-1984">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-1985"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-1986"></div>
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-1987"></div>
                  </div>
                </div>
              </div>}
            <div ref={chatEndRef} data-id="element-1988" />
          </div>
          <div className="p-4 border-t border-gray-700 bg-gray-800" data-id="element-1989">
            <form onSubmit={handleChatSubmit} className="flex space-x-2" data-id="element-1990">
              <input type="text" value={messageInput} onChange={e => setMessageInput(e.target.value)} placeholder="Ask a question about expense policies..." className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" data-id="element-1991" />
              <Button type="submit" disabled={!messageInput.trim() || isTyping} data-id="element-1992">
                {isTyping ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1993"></div> : 'Send'}
              </Button>
            </form>
            <div className="mt-2 text-xs text-gray-400" data-id="element-1994">
              <span data-id="element-1995">Try asking about: </span>
              <button onClick={() => setMessageInput('What are the per diem rates?')} className="text-blue-400 hover:text-blue-300 mr-2" data-id="element-1996">
                per diem rates
              </button>
              <button onClick={() => setMessageInput('When do I need receipts?')} className="text-blue-400 hover:text-blue-300 mr-2" data-id="element-1997">
                receipts
              </button>
              <button onClick={() => setMessageInput('How long do I have to submit expenses?')} className="text-blue-400 hover:text-blue-300" data-id="element-1998">
                submission deadlines
              </button>
            </div>
          </div>
        </Card>
      </div>;
  };
  return <div className="space-y-6" data-id="element-1999">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2000">
        <div data-id="element-2001">
          <h1 className="text-2xl font-bold text-gray-100" data-id="element-2002">Knowledge Base</h1>
          <p className="text-gray-400 mt-1" data-id="element-2003">
            Access policy documents, FAQs, and get assistance with expense
            management
          </p>
        </div>
        <div className="flex items-center space-x-2" data-id="element-2004">
          <Button variant="outline" size="sm" data-id="element-2005">
            <PlusIcon size={16} className="mr-1.5" data-id="element-2006" />
            Request Document
          </Button>
          <Button size="sm" data-id="element-2007">
            <BookmarkIcon size={16} className="mr-1.5" data-id="element-2008" />
            Subscribe to Updates
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-700 overflow-x-auto" data-id="element-2009">
        <button onClick={() => setActiveTab('documents')} className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'documents' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`} data-id="element-2010">
          <FileTextIcon size={16} className="inline-block mr-1.5 -mt-0.5" data-id="element-2011" />
          Policy Documents
        </button>
        <button onClick={() => setActiveTab('faqs')} className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'faqs' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`} data-id="element-2012">
          <HelpCircleIcon size={16} className="inline-block mr-1.5 -mt-0.5" data-id="element-2013" />
          FAQs
        </button>
        <button onClick={() => setActiveTab('chat')} className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'chat' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`} data-id="element-2014">
          <MessageSquareIcon size={16} className="inline-block mr-1.5 -mt-0.5" data-id="element-2015" />
          Chat Assistant
        </button>
      </div>
      {/* Tab content */}
      <div data-id="element-2016">
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'faqs' && renderFaqsTab()}
        {activeTab === 'chat' && renderChatTab()}
      </div>
    </div>;
}